// One rule, used by the run (to mark tasks) and by Analytics (to show the photo next to each task), so both
// always agree on which photo decided a task.

export type PhotoTaskAnswer = {
  suggestion: 'done' | 'attention' | 'review';
  score?: number | null;
  problem?: string | null;
};

type CheckShape = { type?: string; passWhen?: string } | undefined;

/**
 * Does this photo actually show the task? Every photo is checked against every task, so most answers are about
 * tasks that are simply not in the photo. A plain "has this been done?" task counts as shown when Jev saw it
 * done, saw something wrong with it (rubbish, dirty, messy), or leans "done" while unsure. Not seen done with
 * nothing wrong seen, or "can't see it", means the task is not in this photo.
 * Problem questions, choices and scores always count.
 */
export function showsTask(answer: PhotoTaskAnswer, check?: CheckShape) {
  const plain =
    (!check?.type || check.type === 'yesno') && check?.passWhen !== 'no';
  if (!plain) return true;
  if (answer.suggestion === 'done') return true;
  if (answer.problem && answer.problem !== 'not_visible') return true;
  return answer.suggestion === 'review' && (answer.score ?? 0) >= 0.5;
}

const RANK = { done: 3, attention: 2, review: 1 } as const;
const sureness = (a: PhotoTaskAnswer) =>
  a.score == null ? 0 : Math.abs(a.score - 0.5);

/**
 * Of the photos that show a task, the one that decides it: done beats something wrong, which beats unsure; then
 * the surest answer. When one photo saw it done and another saw something wrong, the newest of those two decides
 * (people re-shoot after fixing something), and `conflict` is true.
 * newestFirst: index 0 is the newest photo (Analytics); otherwise the highest index is (the run).
 * Returns undefined when no photo shows the task (it is left as it is).
 */
export function pickPhoto<A extends PhotoTaskAnswer>(
  answers: { answer: A; index: number }[],
  check?: CheckShape,
  newestFirst = false
): { answer: A; index: number; conflict: boolean } | undefined {
  const shown = answers.filter(a => showsTask(a.answer, check));
  if (!shown.length) return undefined;
  const sure = shown.filter(
    a => a.answer.suggestion === 'done' || a.answer.suggestion === 'attention'
  );
  const conflict =
    sure.some(a => a.answer.suggestion === 'done') &&
    sure.some(a => a.answer.suggestion === 'attention');
  if (conflict) {
    const newest = sure.reduce((a, b) =>
      newestFirst ? (b.index < a.index ? b : a) : b.index > a.index ? b : a
    );
    return { ...newest, conflict };
  }
  const best = [...shown].sort(
    (a, b) =>
      RANK[b.answer.suggestion] - RANK[a.answer.suggestion] ||
      sureness(b.answer) - sureness(a.answer)
  )[0];
  return { ...best, conflict: false };
}
